/**
 * User avatar (owner request 2026-10-12): initials of the display name on a deep disc inside a gold
 * ring — the same material as the landing's receipt. No photo upload (no PII storage); without a name a
 * neutral person glyph. Two Persian initials are separated by a middle dot: with only a ZWNJ, م + ن
 * still read as the word «من».
 */
import { BadgeCheck, UserRound } from 'lucide-react';
import { cx } from '../cx.ts';

const SIZES = { sm: 32, md: 40, lg: 76 } as const;
/** Middle dot between two initials: never joins, and never reads as a Persian word. */
const SEP = String.fromCharCode(0x00b7);

export function initialsOf(name: string | null | undefined): string | null {
  const parts = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  if (parts.length === 0) return null;
  const first = [...(parts[0] as string)][0] ?? '';
  const last = parts.length > 1 ? ([...(parts[parts.length - 1] as string)][0] ?? '') : '';
  return last ? first + SEP + last : first;
}

export function Avatar({
  name,
  size = 'md',
  verified,
  className,
}: {
  name?: string | null;
  size?: keyof typeof SIZES;
  /** Shows a small gold check (identity verified). */
  verified?: boolean;
  className?: string;
}) {
  const px = SIZES[size];
  const initials = initialsOf(name);
  return (
    <span
      aria-hidden
      data-avatar
      className={cx(
        'relative inline-grid shrink-0 place-items-center rounded-full p-[2px]',
        className,
      )}
      style={{
        width: px,
        height: px,
        background:
          'conic-gradient(from 210deg, var(--hx-accent), var(--hx-accent-soft), color-mix(in srgb, var(--hx-accent) 35%, transparent), var(--hx-accent))',
      }}
    >
      <span
        className="grid size-full place-items-center rounded-full font-extrabold text-accent-text"
        style={{
          background:
            'radial-gradient(120% 120% at 30% 15%, color-mix(in srgb, var(--hx-accent) 18%, transparent), transparent 60%), var(--hx-bg-deep)',
          fontSize: px * (initials && [...initials].length > 1 ? 0.34 : 0.42),
        }}
      >
        {initials ?? (
          <UserRound className="text-fg-muted" style={{ width: px * 0.48, height: px * 0.48 }} />
        )}
      </span>
      {verified && (
        <span
          className="absolute -bottom-0.5 -end-0.5 grid place-items-center rounded-full bg-canvas"
          style={{ width: px * 0.36, height: px * 0.36, minWidth: 14, minHeight: 14 }}
        >
          <BadgeCheck className="size-full text-accent" strokeWidth={2.2} />
        </span>
      )}
    </span>
  );
}
